import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API_CONFIG from '../../config/api';
import { getDashboardStats } from '../../services/requestService';
import socketService from '../../services/socketService';
import { toast } from 'react-toastify';

interface TestRequest {
  _id: string;
  displayId?: number; // Display ID for tracking (شناسه)
  userId: {
    name: string;
    lastName: string;
    mobile: string;
  };
  patientInfo?: {
    firstName: string;
    lastName: string;
    patientType: string;
  };
  status: string;
  createdAt: string;
  updatedAt: string;
  editVersion?: number;
}

interface DashboardStats {
  totalActiveUsers: number;
  totalActiveRequests: number;
  testsCompletedToday: number;
  totalUsers: number;
  totalRequests: number;
  pendingRequests: number;
  completedRequests: number;
}

// Function to translate status to Persian
const translateStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    reviewing: 'در حال بررسی',
    edited: 'ویرایش شده'
  };
  return statusMap[status] || status;
};

// Function to get status color
const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    reviewing: 'bg-orange-100 text-orange-800',
    edited: 'bg-blue-100 text-blue-800'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

// Function to translate patient type
const translatePatientType = (type: string): string => {
  const typeMap: Record<string, string> = {
    self: 'خودم',
    child: 'فرزند',
    spouse: 'همسر',
    father: 'پدر',
    mother: 'مادر',
    other: 'سایر'
  };
  return typeMap[type] || type;
};

// Admin Current Tasks Component
const AdminCurrentTasks: React.FC = () => {
  const [currentTasks, setCurrentTasks] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchCurrentTasks();
  }, []);

  // Setup socket listener for new test requests
  useEffect(() => {
    const handleNewTestRequest = (notification: any) => {

      // Refresh current tasks
      fetchCurrentTasks();
      
      // Show toast notification
      toast.success(`درخواست جدید از ${notification.testRequest.userInfo} دریافت شد`, {
        position: "top-right",
        autoClose: 5000,
      });
    };

    const handleNewImageReportRequest = (notification: any) => {

      // Refresh current tasks (in case you want to show image reports in dashboard)
      fetchCurrentTasks();
      
      // Show toast notification
      toast.success(`درخواست جدید تصویر جواب از ${notification.imageReportRequest.userInfo} دریافت شد`, {
        position: "top-right",
        autoClose: 5000,
      });
    };

    // Set up socket listeners
    socketService.setOnNewTestRequest(handleNewTestRequest);
    socketService.setOnNewImageReportRequest(handleNewImageReportRequest);

    // Cleanup
    return () => {
      socketService.setOnNewTestRequest(() => {});
      socketService.setOnNewImageReportRequest(() => {});
    };
  }, []);

  const fetchCurrentTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('لطفا ابتدا وارد حساب کاربری خود شوید');
        setLoading(false);
        return;
      }
      
      // Fetch requests with admin current task statuses
      const statuses = ['reviewing', 'edited'];
      const requests: TestRequest[] = [];
      
      for (const status of statuses) {
        const response = await fetch(API_CONFIG.getUrl(`admin/requests?status=${status}&limit=50`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          requests.push(...data.data);
        }
      }
      
      // Sort by most recent first
      requests.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      // Limit to 15 most recent for admin
      setCurrentTasks(requests.slice(0, 15));
    } catch (err) {
      setError('خطا در دریافت کارهای جاری');

    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">کارهای جاری مدیریت</h2>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">کارهای جاری مدیریت</h2>
        <div className="text-center text-red-500 py-8">
          <p>{error}</p>
          <button 
            onClick={fetchCurrentTasks} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">کارهای جاری مدیریت</h2>
        <Link 
          to="/admin/ongoing-requests" 
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          مشاهده همه →
        </Link>
      </div>
      
      {currentTasks.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          <p>هیچ کار جاری‌ای برای بررسی وجود ندارد.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-right py-3 px-2 font-semibold text-sm text-gray-700">شناسه</th>
                <th className="text-right py-3 px-2 font-semibold text-sm text-gray-700">نام کاربر</th>
                <th className="text-right py-3 px-2 font-semibold text-sm text-gray-700">بیمار</th>
                <th className="text-right py-3 px-2 font-semibold text-sm text-gray-700">شماره موبایل</th>
                <th className="text-right py-3 px-2 font-semibold text-sm text-gray-700">وضعیت</th>
                <th className="text-right py-3 px-2 font-semibold text-sm text-gray-700">تاریخ</th>
                <th className="text-right py-3 px-2 font-semibold text-sm text-gray-700">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {currentTasks.map(request => (
                <tr key={request._id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-2 text-sm font-medium text-gray-900">
                    {request.displayId || request._id.slice(-8)}
                  </td>
                  <td className="py-3 px-2 text-sm">
                    <div className="font-medium text-gray-900">
                      {request.userId.name} {request.userId.lastName}
                    </div>
                  </td>
                  <td className="py-3 px-2 text-sm">
                    {request.patientInfo ? (
                      <div>
                        <div className="font-medium text-gray-900">
                          {request.patientInfo.firstName} {request.patientInfo.lastName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {translatePatientType(request.patientInfo.patientType)}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500">---</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-900">
                    {request.userId.mobile}
                  </td>
                  <td className="py-3 px-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(request.status)}`}>
                        {translateStatus(request.status)}
                      </span>
                      {request.editVersion && request.editVersion > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800" title={`ویرایش ${request.editVersion}`}>
                          {request.editVersion}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-500">
                    {new Date(request.createdAt).toLocaleDateString('fa-IR')}
                  </td>
                  <td className="py-3 px-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Link 
                        to="/admin/ongoing-requests"
                        state={{ requestId: request._id }}
                        className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-200 transition-colors"
                      >
                        بررسی
                      </Link>
                      {request.status === 'reviewing' && (
                        <div className="flex gap-1">
                         
                        </div>
                      )}
                      {request.status === 'edited' && request.editVersion && request.editVersion > 0 && (
                        <div className="flex gap-1">
                         
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalActiveUsers: 0,
    totalActiveRequests: 0,
    testsCompletedToday: 0,
    totalUsers: 0,
    totalRequests: 0,
    pendingRequests: 0,
    completedRequests: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState<string>('');

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoadingStats(true);
      setStatsError('');
      
      const response = await getDashboardStats();
      
      if (response.success) {
        setStats(response.data);
      } else {
        setStatsError('خطا در دریافت آمار');
      }
    } catch (error) {

      setStatsError('خطا در دریافت آمار');
    } finally {
      setLoadingStats(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">داشبورد مدیریت</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 rounded-lg p-4 shadow">
            <h3 className="text-lg font-semibold text-blue-700 mb-2">کاربران</h3>
            {loadingStats ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : statsError ? (
              <p className="text-red-500 text-sm">خطا در دریافت داده</p>
            ) : (
              <>
                <p className="text-3xl font-bold">{stats.totalActiveUsers}</p>
                <p className="text-sm text-gray-600">کاربر فعال در سامانه</p>
              </>
            )}
          </div>
          
          <div className="bg-green-50 rounded-lg p-4 shadow">
            <h3 className="text-lg font-semibold text-green-700 mb-2">درخواست ها</h3>
            {loadingStats ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-green-500"></div>
              </div>
            ) : statsError ? (
              <p className="text-red-500 text-sm">خطا در دریافت داده</p>
            ) : (
              <>
                <p className="text-3xl font-bold">{stats.totalActiveRequests}</p>
                <p className="text-sm text-gray-600">درخواست در حال بررسی</p>
              </>
            )}
          </div>
          
          <div className="bg-purple-50 rounded-lg p-4 shadow">
            <h3 className="text-lg font-semibold text-purple-700 mb-2">آزمایش‌ها</h3>
            {loadingStats ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            ) : statsError ? (
              <p className="text-red-500 text-sm">خطا در دریافت داده</p>
            ) : (
              <>
                <p className="text-3xl font-bold">{stats.testsCompletedToday}</p>
                <p className="text-sm text-gray-600">آزمایش انجام شده امروز</p>
              </>
            )}
          </div>
        </div>
        
        {statsError && (
          <div className="mt-4 text-center">
            <button 
              onClick={fetchDashboardStats}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              بارگذاری مجدد آمار
            </button>
          </div>
        )}
      </div>

      {/* Admin Current Tasks Table */}
      <AdminCurrentTasks />
    </div>
  );
};

export default AdminDashboard;

